import { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import PageHeader from "../components/PageHeader";
import SurfaceCard from "../components/SurfaceCard";
import { useFeedback } from "../components/FeedbackProvider";
import { deleteEnquiry, getEnquiries, setEnquiryStatus } from "firestoreService";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "in-progress", label: "In progress" },
  { key: "closed", label: "Closed" },
];

const STATUS_TONE = {
  new: { color: "warning", label: "New" },
  "in-progress": { color: "info", label: "In progress" },
  closed: { color: "success", label: "Closed" },
};

const formatStamp = (value) => {
  if (typeof value !== "string") return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value.slice(0, 10);
  return parsed.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

function Enquiries() {
  const { notify, confirm } = useFeedback();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await getEnquiries());
    } catch (loadError) {
      console.error("Could not load enquiries:", loadError);
      setError("Could not load enquiries.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (filter !== "all" && row.status !== filter) return false;
      if (!needle) return true;
      return [row.name, row.email, row.mobileNo, row.subject, row.message]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(needle));
    });
  }, [rows, filter, search]);

  const counts = useMemo(
    () =>
      rows.reduce(
        (acc, row) => ({ ...acc, [row.status]: (acc[row.status] ?? 0) + 1, all: acc.all + 1 }),
        { all: 0 }
      ),
    [rows]
  );

  const changeStatus = async (row, status) => {
    // Update locally first so the inbox does not flicker through a full
    // reload for a one-field change; the reload only happens on failure.
    setRows((current) => current.map((item) => (item.id === row.id ? { ...item, status } : item)));
    setSelected((current) => (current?.id === row.id ? { ...current, status } : current));
    try {
      await setEnquiryStatus(row.id, status);
      notify(`Marked as ${STATUS_TONE[status].label.toLowerCase()}.`);
    } catch (updateError) {
      console.error("Could not update enquiry:", updateError);
      notify("Could not update the enquiry.", "error");
      load();
    }
  };

  const removeEnquiry = async (row) => {
    const confirmed = await confirm({
      title: "Delete this enquiry?",
      message: `The enquiry from ${row.name || "this visitor"} will be permanently removed.`,
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!confirmed) return;

    try {
      await deleteEnquiry(row.id);
      setRows((current) => current.filter((item) => item.id !== row.id));
      setSelected((current) => (current?.id === row.id ? null : current));
      notify("Enquiry deleted.");
    } catch (deleteError) {
      console.error("Could not delete enquiry:", deleteError);
      notify("Could not delete the enquiry.", "error");
    }
  };

  return (
    <>
      <PageHeader
        title="Enquiries"
        description="Messages sent from the Contact Us page."
        actions={
          <Button
            onClick={load}
            startIcon={<RefreshIcon />}
            variant="outlined"
            sx={{ textTransform: "none" }}
            disabled={loading}
          >
            Refresh
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} action={<Button onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      )}

      <SurfaceCard padded={false}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
          sx={{ px: 2, pt: 1.5 }}
        >
          <Tabs
            value={filter}
            onChange={(event, value) => setFilter(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: 40 }}
          >
            {FILTERS.map((item) => (
              <Tab
                key={item.key}
                value={item.key}
                label={`${item.label} (${counts[item.key] ?? 0})`}
                sx={{ textTransform: "none", minHeight: 40, fontSize: "0.85rem" }}
              />
            ))}
          </Tabs>

          <TextField
            size="small"
            placeholder="Search name, email, message"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ minWidth: { md: 280 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        <Divider sx={{ mt: 1.5 }} />

        {loading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : visible.length === 0 ? (
          <Typography sx={{ py: 8, textAlign: "center", color: "#7b809a" }}>
            {rows.length === 0 ? "No enquiries yet." : "No enquiries match this filter."}
          </Typography>
        ) : (
          visible.map((row) => (
            <Stack
              key={row.id}
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ sm: "center" }}
              sx={{
                px: 2,
                py: 1.75,
                borderBottom: "1px solid #f0f2f5",
                cursor: "pointer",
                bgcolor: row.status === "new" ? "#fffdf6" : "transparent",
                "&:hover": { bgcolor: "#f4f6fa" },
                "&:last-of-type": { borderBottom: 0 },
              }}
              onClick={() => setSelected(row)}
            >
              <Avatar sx={{ width: 38, height: 38, bgcolor: "#174276", fontSize: "0.9rem" }}>
                {(row.name ?? "?")[0]?.toUpperCase()}
              </Avatar>

              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    noWrap
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: row.status === "new" ? 700 : 600,
                      color: "#1b2a41",
                    }}
                  >
                    {row.name || "Unnamed"}
                  </Typography>
                  <Chip
                    size="small"
                    label={STATUS_TONE[row.status]?.label ?? row.status}
                    color={STATUS_TONE[row.status]?.color ?? "default"}
                    sx={{ height: 20, fontSize: "0.68rem" }}
                  />
                </Stack>
                <Typography noWrap sx={{ fontSize: "0.85rem", color: "#1b2a41" }}>
                  {row.subject}
                </Typography>
                <Typography noWrap sx={{ fontSize: "0.78rem", color: "#7b809a" }}>
                  {row.message}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                <Typography sx={{ fontSize: "0.72rem", color: "#7b809a", whiteSpace: "nowrap" }}>
                  {formatStamp(row.createdAt)}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    removeEnquiry(row);
                  }}
                  aria-label="Delete enquiry"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          ))
        )}
      </SurfaceCard>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        {selected && (
          <>
            <DialogTitle sx={{ pr: 6 }}>
              {selected.subject}
              <IconButton
                onClick={() => setSelected(null)}
                sx={{ position: "absolute", right: 12, top: 12 }}
                aria-label="Close"
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={0.5} sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 700, color: "#1b2a41" }}>{selected.name}</Typography>
                <Typography sx={{ fontSize: "0.85rem", color: "#7b809a" }}>
                  <a href={`mailto:${selected.email}`}>{selected.email}</a>
                  {" · "}
                  <a href={`tel:${selected.mobileNo}`}>{selected.mobileNo}</a>
                </Typography>
                <Typography sx={{ fontSize: "0.78rem", color: "#7b809a" }}>
                  Received {formatStamp(selected.createdAt)}
                </Typography>
              </Stack>

              <Typography sx={{ whiteSpace: "pre-wrap", fontSize: "0.92rem", color: "#1b2a41" }}>
                {selected.message}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography sx={{ fontSize: "0.78rem", color: "#7b809a", mb: 1 }}>
                Mark this enquiry as
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {Object.entries(STATUS_TONE).map(([key, tone]) => (
                  <Button
                    key={key}
                    size="small"
                    variant={selected.status === key ? "contained" : "outlined"}
                    color={tone.color}
                    onClick={() => changeStatus(selected, key)}
                    sx={{
                      textTransform: "none",
                      color: selected.status === key ? "#fff" : undefined,
                    }}
                  >
                    {tone.label}
                  </Button>
                ))}
              </Stack>
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
}

export default Enquiries;
